import { useState, useEffect } from "react";
import { apiCall } from "./api";
import { db, collection, doc, onSnapshot } from "./firebase";
import { FONT_BODY, FONT_DISPLAY, ELLIPSIS } from "./theme";
import { SpectatingBadge, leaveLobbyBody } from "./spectator";
import HouseRulesCard from "./house-rules-card";

// ============================================================
// BLACK OWL - Lobby Screen
// Host selects NFL matchups, players join, draft starts here
// ============================================================

// --- Owl icon (header) ---
function OwlIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="60" cy="68" rx="38" ry="42" fill="#1a1a2e" stroke="#c9a227" strokeWidth="2.5"/>
      <polygon points="30,30 38,48 22,45" fill="#1a1a2e" stroke="#c9a227" strokeWidth="2"/>
      <polygon points="90,30 82,48 98,45" fill="#1a1a2e" stroke="#c9a227" strokeWidth="2"/>
      <circle cx="44" cy="55" r="14" fill="#0d0d1a" stroke="#c9a227" strokeWidth="2"/>
      <circle cx="76" cy="55" r="14" fill="#0d0d1a" stroke="#c9a227" strokeWidth="2"/>
      <circle cx="44" cy="55" r="8" fill="#c9a227"/>
      <circle cx="76" cy="55" r="8" fill="#c9a227"/>
      <circle cx="44" cy="55" r="4" fill="#0d0d1a"/>
      <circle cx="76" cy="55" r="4" fill="#0d0d1a"/>
      <circle cx="47" cy="52" r="2" fill="#fff" opacity="0.7"/>
      <circle cx="79" cy="52" r="2" fill="#fff" opacity="0.7"/>
      <polygon points="55,65 60,75 65,65" fill="#c9a227"/>
    </svg>
  );
}

// --- Background ---
function FieldBackground() {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 0, overflow: "hidden",
      background: "linear-gradient(165deg, #0a0a14 0%, #0f1a0f 40%, #0a0a14 100%)",
    }}>
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} style={{
          position: "absolute", left: 0, right: 0,
          top: `${(i + 1) * 8}%`, height: "1px",
          background: `rgba(201, 162, 39, ${i === 5 ? 0.12 : 0.04})`,
        }}/>
      ))}
      <div style={{
        position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.6) 100%)",
      }}/>
    </div>
  );
}

// --- Player chip (lobby member) ---
function PlayerChip({ name, isHost, isYou }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: "8px",
      padding: "10px 16px", maxWidth: "100%", boxSizing: "border-box",
      background: isYou ? "rgba(201, 162, 39, 0.08)" : "rgba(255,255,255,0.03)",
      border: `1px solid ${isYou ? "rgba(201, 162, 39, 0.2)" : "rgba(255,255,255,0.06)"}`,
      borderRadius: "10px",
    }}>
      <div style={{
        width: "8px", height: "8px", borderRadius: "50%", flexShrink: 0,
        background: "#32c850",
        boxShadow: "0 0 6px rgba(50, 200, 80, 0.4)",
      }}/>
      <span style={{
        fontSize: "14px", color: "#e8e6df",
        fontFamily: FONT_BODY, ...ELLIPSIS,
      }}>{name}</span>
      {isHost && (
        <span style={{
          fontSize: "9px", fontWeight: 700, letterSpacing: "1.5px",
          textTransform: "uppercase", color: "#c9a227",
          fontFamily: FONT_BODY,
          background: "rgba(201, 162, 39, 0.12)",
          padding: "2px 6px", borderRadius: "3px", flexShrink: 0,
        }}>HOST</span>
      )}
      {isYou && !isHost && (
        <span style={{
          fontSize: "9px", fontWeight: 700, letterSpacing: "1.5px",
          textTransform: "uppercase", color: "#5a5a6e",
          fontFamily: FONT_BODY,
        }}>YOU</span>
      )}
    </div>
  );
}

// --- Section header ---
function SectionHeader({ title, subtitle, rightElement }) {
  return (
    <div style={{
      display: "flex", alignItems: "flex-end", justifyContent: "space-between",
      marginBottom: "16px",
    }}>
      <div>
        <h3 style={{
          fontSize: "13px", fontWeight: 700, letterSpacing: "2px",
          textTransform: "uppercase", color: "#c9a227",
          margin: "0 0 2px", fontFamily: FONT_BODY,
        }}>{title}</h3>
        {subtitle && (
          <p style={{
            fontSize: "12px", color: "#5a5a6e", margin: 0,
            fontFamily: FONT_BODY,
          }}>{subtitle}</p>
        )}
      </div>
      {rightElement}
    </div>
  );
}

// --- Main Lobby Screen ---
export default function LobbyScreen({ username, lobbyId, isHost, isSpectator, onLeaveLobby, onStartDraft }) {
  const [players, setPlayers] = useState([]);
  const [matchIds, setMatchIds] = useState([]);
  const [matchupNames, setMatchupNames] = useState({});
  const [loadingAction, setLoadingAction] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  // FB-24: House Rules live on the lobby doc; only lobby_owner may edit
  const [houseRules, setHouseRules] = useState({});
  const [lobbyOwner, setLobbyOwner] = useState("");
  const [rulesLocked, setRulesLocked] = useState(false);

  // Real-time listener for lobby players
  useEffect(() => {
    // Listen for players joining/leaving the lobby
    const playersRef = collection(db, "Lobbies", lobbyId, "players");
    const unsubPlayers = onSnapshot(playersRef, (snapshot) => {
      const playerList = snapshot.docs.map((doc) => ({
        name: doc.id,
        isHost: false, // we'll fix this from lobby data
        ...doc.data(),
      }));
      setPlayers(playerList);
    });

    // Listen for lobby state changes (draft starting, etc.)
    const lobbyRef = doc(db, "Lobbies", lobbyId);
    const unsubLobby = onSnapshot(lobbyRef, (snapshot) => {
      const data = snapshot.data();
      if (data) {
        // Capture match IDs for draft info display
        setMatchIds(data.match_ids || []);
        setHouseRules(data.house_rules || {});
        setLobbyOwner(data.lobby_owner || "");
        setRulesLocked(Boolean(data.ready_to_play));
        // If draft has started, navigate all players to draft screen
        if (data.ready_to_draft && onStartDraft) {
          onStartDraft(lobbyId);
        }
        // Update host info in players list
        setPlayers((prev) =>
          prev.map((p) => ({
            ...p,
            isHost: p.name === data.lobby_owner,
          }))
        );
      }
    });

    // Listen for match_roster to derive team names per matchup
    const rosterRef = collection(db, "Lobbies", lobbyId, "match_roster");
    const unsubRoster = onSnapshot(rosterRef, (snapshot) => {
      // Group teams by team_id, then we'll need to figure out matchup pairing
      const teamsByMatch = {}; // { team_id: team_name }
      snapshot.docs.forEach((doc) => {
        const p = doc.data();
        if (p.team_id && p.team) {
          teamsByMatch[p.team_id] = p.team;
        }
      });
      setMatchupNames(teamsByMatch);
    });

    // Cleanup listeners when leaving this screen
    return () => {
      unsubPlayers();
      unsubLobby();
      unsubRoster();
    };
  }, [lobbyId]);

  const copyLobbyCode = () => {
    navigator.clipboard.writeText(lobbyId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleStartDraft = async () => {
    setError("");
    setLoadingAction(true);
    try {
      const result = await apiCall("startDraft", { lobby_id: lobbyId });
      if (result.status === "success" && onStartDraft) {
        onStartDraft(lobbyId);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleLeaveLobby = async () => {
    if (!isSpectator && !window.confirm("Leave this lobby? This cannot be undone.")) return;
    setLoadingAction(true);
    try {
      await apiCall("leave_lobby", leaveLobbyBody(username, lobbyId, isSpectator));
      if (onLeaveLobby) onLeaveLobby();
    } catch (err) {
      setError(err.message);
      if (onLeaveLobby) onLeaveLobby();
    } finally {
      setLoadingAction(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      fontFamily: FONT_BODY, position: "relative",
      paddingTop: "40px", paddingBottom: "40px",
    }}>
      <FieldBackground />

      <div style={{
        position: "relative", zIndex: 1,
        width: "100%", maxWidth: "480px", padding: "0 20px",
      }}>
        {/* Header card */}
        <div style={{
          background: "rgba(12, 12, 22, 0.85)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(201, 162, 39, 0.15)",
          borderRadius: "16px",
          padding: "24px 28px",
          boxShadow: "0 24px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(201,162,39,0.08)",
          marginBottom: "16px",
        }}>
          {/* Top bar */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            marginBottom: "20px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <OwlIcon />
              <div>
                <h1 style={{
                  fontSize: "16px", fontWeight: 700, color: "#c9a227",
                  margin: 0, letterSpacing: "2px", textTransform: "uppercase",
                  fontFamily: FONT_DISPLAY,
                }}>Game Lobby</h1>
              </div>
              {isSpectator && <SpectatingBadge />}
            </div>
            <button
              onClick={handleLeaveLobby}
              disabled={loadingAction}
              style={{
                padding: "8px 14px", fontSize: "11px",
                fontWeight: 600, letterSpacing: "1.5px",
                textTransform: "uppercase",
                fontFamily: FONT_BODY,
                color: "#e85555", background: "rgba(220, 50, 50, 0.08)",
                border: "1px solid rgba(220, 50, 50, 0.2)",
                borderRadius: "6px", cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              Leave
            </button>
          </div>

          {/* Lobby code display */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "16px 20px",
            background: "rgba(201, 162, 39, 0.04)",
            border: "1px dashed rgba(201, 162, 39, 0.2)",
            borderRadius: "10px",
          }}>
            <div>
              <p style={{
                fontSize: "10px", fontWeight: 600, letterSpacing: "2px",
                textTransform: "uppercase", color: "#5a5a6e",
                margin: "0 0 4px", fontFamily: FONT_BODY,
              }}>Lobby Code</p>
              {/* One fixed-width cell per character so the code reads evenly aloud */}
              <p aria-label={`Lobby code ${lobbyId}`} style={{
                display: "flex", gap: "4px",
                fontSize: "34px", fontWeight: 700, color: "#c9a227",
                margin: 0, lineHeight: 1.1,
                fontFamily: FONT_BODY, fontVariantNumeric: "tabular-nums",
              }}>
                {String(lobbyId).split("").map((ch, i) => (
                  <span key={i} style={{ width: "0.8em", textAlign: "center" }}>{ch}</span>
                ))}
              </p>
            </div>
            <button
              onClick={copyLobbyCode}
              style={{
                padding: "10px 16px", fontSize: "11px",
                fontWeight: 600, letterSpacing: "1px",
                fontFamily: FONT_BODY,
                color: copied ? "#32c850" : "#c9a227",
                background: copied ? "rgba(50, 200, 80, 0.1)" : "rgba(201, 162, 39, 0.1)",
                border: `1px solid ${copied ? "rgba(50, 200, 80, 0.3)" : "rgba(201, 162, 39, 0.25)"}`,
                borderRadius: "6px", cursor: "pointer",
                transition: "all 0.3s ease",
              }}
            >
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>

        {/* Players section */}
        <div style={{
          background: "rgba(12, 12, 22, 0.85)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(201, 162, 39, 0.15)",
          borderRadius: "16px",
          padding: "24px 28px",
          boxShadow: "0 24px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(201,162,39,0.08)",
          marginBottom: "16px",
        }}>
          <SectionHeader
            title="Players"
            subtitle={`${players.length} in lobby`}
          />
          <div style={{
            display: "flex", flexWrap: "wrap", gap: "8px",
          }}>
            {players.map((p) => (
              <PlayerChip
                key={p.name}
                name={p.name}
                isHost={p.isHost}
                isYou={p.name === username}
              />
            ))}
          </div>
          <p style={{
            fontSize: "12px", color: "#3a3a4e", marginTop: "14px", marginBottom: 0,
            fontFamily: FONT_BODY, fontStyle: "italic",
          }}>
            Share the lobby code with your crew to join...
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            padding: "12px 16px", marginBottom: "16px",
            background: "rgba(220, 50, 50, 0.1)",
            border: "1px solid rgba(220, 50, 50, 0.25)",
            borderRadius: "8px", color: "#e85555",
            fontSize: "13px", fontFamily: FONT_BODY,
          }}>{error}</div>
        )}

        {/* Draft setup info - visible to everyone */}
        {players.length >= 1 && matchIds.length > 0 && (() => {
          const numTeams = matchIds.length * 2;
          const numUsers = players.length;
          const bottleneckDups = Math.ceil(numUsers / numTeams);
          const wrDups = Math.ceil(numUsers / (2 * numTeams));
          return (
            <div style={{
              background: "rgba(12, 12, 22, 0.85)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(201, 162, 39, 0.15)",
              borderRadius: "16px",
              padding: "20px 24px",
              boxShadow: "0 24px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(201,162,39,0.08)",
              marginBottom: "16px",
            }}>
              <p style={{
                fontSize: "11px", fontWeight: 700, letterSpacing: "2px",
                textTransform: "uppercase", color: "#c9a227",
                margin: "0 0 12px", fontFamily: FONT_BODY,
              }}>Draft Setup</p>
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px",
              }}>
                <div style={{
                  padding: "10px 12px",
                  background: "rgba(255,255,255,0.02)",
                  borderRadius: "8px",
                }}>
                  <p style={{
                    fontSize: "10px", color: "#5a5a6e", margin: "0 0 4px",
                    fontFamily: FONT_BODY,
                    letterSpacing: "1px", textTransform: "uppercase",
                  }}>Users</p>
                  <p style={{
                    fontSize: "18px", fontWeight: 700, color: "#e8e6df",
                    margin: 0, fontFamily: FONT_BODY,
                  }}>{numUsers}</p>
                </div>
                <div style={{
                  padding: "10px 12px",
                  background: "rgba(255,255,255,0.02)",
                  borderRadius: "8px",
                }}>
                  <p style={{
                    fontSize: "10px", color: "#5a5a6e", margin: "0 0 4px",
                    fontFamily: FONT_BODY,
                    letterSpacing: "1px", textTransform: "uppercase",
                  }}>Teams</p>
                  <p style={{
                    fontSize: "18px", fontWeight: 700, color: "#e8e6df",
                    margin: 0, fontFamily: FONT_BODY,
                  }}>{numTeams} ({matchIds.length} game{matchIds.length !== 1 ? "s" : ""})</p>
                </div>
              </div>
              <div style={{
                marginTop: "10px", padding: "12px",
                background: "rgba(201, 162, 39, 0.06)",
                border: "1px solid rgba(201, 162, 39, 0.15)",
                borderRadius: "8px",
              }}>
                <p style={{
                  fontSize: "11px", color: "#c9a227",
                  margin: "0 0 6px", fontFamily: FONT_BODY,
                  letterSpacing: "1px", textTransform: "uppercase",
                  fontWeight: 700,
                }}>Player Availability</p>
                <p style={{
                  fontSize: "12px", color: "#a0a0b0", margin: "0 0 4px",
                  fontFamily: FONT_BODY,
                }}>
                  Each QB/RB/TE/K draftable <strong style={{ color: "#c9a227" }}>{bottleneckDups}x</strong>
                </p>
                <p style={{
                  fontSize: "12px", color: "#a0a0b0", margin: 0,
                  fontFamily: FONT_BODY,
                }}>
                  Each WR draftable <strong style={{ color: "#c9a227" }}>{wrDups}x</strong>
                </p>
              </div>

              {/* Matchups list */}
              {Object.keys(matchupNames).length > 0 && (
                <div style={{
                  marginTop: "10px", padding: "12px",
                  background: "rgba(74, 158, 255, 0.06)",
                  border: "1px solid rgba(74, 158, 255, 0.15)",
                  borderRadius: "8px",
                }}>
                  <p style={{
                    fontSize: "11px", color: "#4a9eff",
                    margin: "0 0 8px", fontFamily: FONT_BODY,
                    letterSpacing: "1px", textTransform: "uppercase",
                    fontWeight: 700,
                  }}>Selected Matchups</p>
                  {Object.values(matchupNames).map((teamName, i, arr) => (
                    <span key={i} style={{
                      display: "inline-block",
                      fontSize: "12px", color: "#e8e6df",
                      fontFamily: FONT_BODY,
                      padding: "2px 0",
                    }}>
                      {teamName}{i < arr.length - 1 ? " · " : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* House Rules (FB-24): host edits, everyone else reads */}
        <HouseRulesCard
          lobbyId={lobbyId}
          username={username}
          rules={houseRules}
          canEdit={!isSpectator && lobbyOwner === username}
          locked={rulesLocked}
        />

        {/* Start Draft button (host only) */}
        {isHost && (
          <button
            onClick={handleStartDraft}
            disabled={loadingAction || players.length < 2}
            style={{
              width: "100%", padding: "18px",
              fontSize: "15px", fontWeight: 700,
              letterSpacing: "3px", textTransform: "uppercase",
              fontFamily: FONT_BODY,
              color: players.length < 2 ? "#5a5a6e" : "#0a0a14",
              background: players.length < 2
                ? "rgba(255,255,255,0.05)"
                : loadingAction
                  ? "rgba(201, 162, 39, 0.5)"
                  : "linear-gradient(135deg, #c9a227 0%, #e0be4a 50%, #c9a227 100%)",
              border: players.length < 2
                ? "1px solid rgba(255,255,255,0.08)"
                : "none",
              borderRadius: "12px",
              cursor: players.length < 2 || loadingAction ? "default" : "pointer",
              transition: "all 0.3s ease",
              boxShadow: players.length >= 2 && !loadingAction
                ? "0 6px 30px rgba(201, 162, 39, 0.3)"
                : "none",
              marginBottom: "16px",
            }}
          >
            {loadingAction ? "Starting..." : players.length < 2 ? "Waiting for players..." : "Start Draft"}
          </button>
        )}

        {/* Footer */}
        <p style={{
          textAlign: "center", marginTop: "8px",
          fontSize: "11px", color: "#3a3a4e",
          letterSpacing: "1px", fontFamily: FONT_BODY,
        }}>
          Draft smart. Play bold. Win big.
        </p>
      </div>

      <style>{`* { box-sizing: border-box; } input::placeholder { color: #3a3a4e; }`}</style>
    </div>
  );
}
